import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router";
import { useEndpoints } from "../../api/ApiContext";
import type { ProviderDecision, ProviderDecisionRequest, ProviderDecisionResponse, ProviderOperation } from "../../api/staffTypes";
import { Banner, Button, Card, EmptyState, Input, PageHeader, QueryView, RadioGroup, StatusPill, Table, Textarea } from "../../components/ui";
import { staffKeys } from "../../layout/StaffShell";
import {
  Amount,
  AwaitingSecondBanner,
  DualControlProgress,
  MoneyConfirmDialog,
  Mono,
  parseEvidence,
  reasonError,
  shortId,
  StaffRejection,
  StepUpNote,
  useStaffCommand,
  when,
} from "./staffCommon";

// Provider operations the machine can't resolve (0043, 0050;
// provider_operations.go): every 'unknown' operation and every finalized
// one carrying a manual-review flag, with suggestions from the Stripe
// inbox (leads only), and the human decision. RequireRecentMFA. 'failed'
// and 'double_payment' need two distinct reviewers whose votes name the
// same attached object (0050:1913-1928, 1979-1996). Refusals are 409
// request_rejected with the SQL's own words as `detail`.

function money(op: ProviderOperation): { amount: string | null; currency: string | null } {
  if (op.payout) return { amount: op.payout.amount, currency: op.payout.currency };
  if (op.refund) return { amount: op.refund.amount, currency: op.refund.currency };
  if (op.transfer_reversal) return { amount: op.transfer_reversal.amount, currency: null };
  return { amount: null, currency: null };
}

/** Distinct reviewers whose vote for `decision` still counts: cast against the object attached now. */
function countingVotes(op: ProviderOperation, decision: "failed" | "double_payment") {
  const seen = new Set<string>();
  return op.decisions.filter((v) => {
    if (v.decision !== decision || v.attached_object_id_at_decision !== op.provider_object_id || seen.has(v.actor_user_id)) return false;
    seen.add(v.actor_user_id);
    return true;
  });
}

function allowedDecisions(op: ProviderOperation): ProviderDecision[] {
  if (op.status === "unknown") return ["confirmed", "failed"];
  const out: ProviderDecision[] = [];
  if (op.manual_review_reason) out.push("acknowledged");
  if (op.status === "failed" && (op.operation_type === "payout" || op.operation_type === "refund") && op.provider_object_id) out.push("double_payment");
  return out;
}

export function ProviderOperationsPage() {
  const ep = useEndpoints();
  const q = useQuery({ queryKey: staffKeys.providerOps, queryFn: () => ep.staff.providerOperations.unresolved() });
  return (
    <div className="stack-lg">
      <PageHeader
        title="Provider operations"
        description="Payouts, refunds and transfer reversals whose outcome at Stripe is unknown, or finalized with a flag. Verify in Stripe before deciding."
      />
      <QueryView query={q} loadingLabel="Loading provider operations">
        {(data) => (
          <Table
            caption="Unresolved and flagged operations"
            rows={data.operations}
            rowKey={(o) => o.provider_operation_id}
            empty={<EmptyState title="Nothing unresolved">No operation is unknown or flagged.</EmptyState>}
            columns={[
              {
                key: "id",
                header: "Operation",
                cell: (o) => <Link to={`/staff/provider-operations/${o.provider_operation_id}`}>{`${o.operation_type.replace(/_/g, " ")} ${shortId(o.provider_operation_id)}`}</Link>,
              },
              { key: "status", header: "Status", cell: (o) => <StatusPill tone={o.status === "unknown" ? "warning" : "neutral"}>{o.status}</StatusPill> },
              { key: "amount", header: "Amount", numeric: true, cell: (o) => <Amount value={money(o).amount} currency={money(o).currency} /> },
              { key: "flag", header: "Flag", cell: (o) => o.manual_review_reason ?? "—" },
              { key: "obj", header: "Stripe object", cell: (o) => <Mono>{o.provider_object_id ?? "none"}</Mono> },
              { key: "votes", header: "Decisions", numeric: true, cell: (o) => o.decisions.length },
              { key: "created", header: "Created", cell: (o) => when(o.created_at) },
            ]}
          />
        )}
      </QueryView>
    </div>
  );
}

export function ProviderOperationDetailPage() {
  const { operationId = "" } = useParams();
  const ep = useEndpoints();
  // Migration 0068 (G-57): a single-operation read, for any status, not
  // just unresolved. Before this the detail page could only show an
  // operation that was still in the unresolved list; a decided one
  // dropped out of view entirely.
  const q = useQuery({ queryKey: ["staff-provider-op", operationId], queryFn: () => ep.staff.providerOperations.get(operationId) });
  return (
    <div className="stack-lg">
      <PageHeader title={`Provider operation ${shortId(operationId)}`} back={{ to: "/staff/provider-operations", label: "Provider operations" }} />
      <QueryView query={q} loadingLabel="Loading the operation">
        {(op) => <OperationDetail op={op} />}
      </QueryView>
    </div>
  );
}

function OperationDetail({ op }: { op: ProviderOperation }) {
  const m = money(op);
  const failedVotes = countingVotes(op, "failed");
  const doubleVotes = countingVotes(op, "double_payment");
  return (
    <div className="stack">
      <Card title="Operation">
        <dl className="definition-list">
          <dt>Type / status</dt>
          <dd>
            {op.operation_type.replace(/_/g, " ")} · {op.status}
          </dd>
          <dt>Amount</dt>
          <dd>
            <Amount value={m.amount} currency={m.currency} />
          </dd>
          <dt>Our reference</dt>
          <dd className="mono">{op.our_reference_id}</dd>
          <dt>Provider idempotency key</dt>
          <dd className="mono break-all">{op.provider_idempotency_key}</dd>
          <dt>Attached Stripe object</dt>
          <dd className="mono">{op.provider_object_id ?? "none"}</dd>
          <dt>First sent / recovery deadline</dt>
          <dd>
            {when(op.submission_attempted_at)} / {when(op.recovery_deadline)}
          </dd>
          <dt>Flag</dt>
          <dd>{op.manual_review_reason ? `${op.manual_review_reason} (${when(op.manual_review_at)})` : "None"}</dd>
          {op.payout ? (
            <>
              <dt>Payout</dt>
              <dd>
                <Mono>{op.payout.payout_id}</Mono> · {op.payout.state} · publisher <Mono>{shortId(op.payout.publisher_id)}</Mono> · Connect{" "}
                <Mono>{op.payout.stripe_connect_account_id ?? "none"}</Mono>
              </dd>
            </>
          ) : null}
          {op.refund ? (
            <>
              <dt>Refund</dt>
              <dd>
                <Mono>{op.refund.refund_id}</Mono> · {op.refund.state} · encumbered <Amount value={op.refund.encumbered_amount} currency={op.refund.currency} /> ·
                PaymentIntent <Mono>{op.refund.stripe_payment_intent_id ?? "none"}</Mono>
              </dd>
            </>
          ) : null}
          {op.transfer_reversal ? (
            <>
              <dt>Transfer reversal</dt>
              <dd>
                <Mono>{op.transfer_reversal.transfer_reversal_id}</Mono> · {op.transfer_reversal.state} · debt case{" "}
                <Link to={`/staff/debt-cases/${op.transfer_reversal.case_id}`}>{shortId(op.transfer_reversal.case_id)}</Link>
              </dd>
            </>
          ) : null}
        </dl>
      </Card>
      <Card title="Suggestions from the Stripe inbox" subtitle="Leads to verify in Stripe. Nothing finalizes from a suggestion.">
        <Table
          caption="Suggested Stripe objects"
          captionHidden
          rows={op.suggestions}
          rowKey={(s) => s.provider_object_id}
          empty={<p className="muted small">No unattached Stripe object matches this destination.</p>}
          columns={[
            { key: "obj", header: "Object", cell: (s) => <Mono>{s.provider_object_id}</Mono> },
            { key: "type", header: "Last event", cell: (s) => s.last_event_type },
            { key: "status", header: "Status", cell: (s) => s.status ?? "—" },
            { key: "amt", header: "Amount (Stripe minor units)", numeric: true, cell: (s) => (s.amount === null ? "—" : `${s.amount} ${s.currency ?? ""}`) },
          ]}
        />
      </Card>
      <Card title="Decisions so far">
        {op.status === "unknown" ? (
          <DualControlProgress label="Votes to finalize failed" approvals={failedVotes.length} required={2} votes={failedVotes} />
        ) : null}
        {op.status === "failed" && op.provider_object_id ? (
          <DualControlProgress label="Votes for double payment" approvals={doubleVotes.length} required={2} votes={doubleVotes} />
        ) : null}
        <Table
          caption="Every manual decision on this operation"
          rows={op.decisions}
          rowKey={(v) => `${v.actor_user_id}-${v.decided_at}`}
          empty={<p className="muted small">No decisions yet.</p>}
          columns={[
            { key: "who", header: "Reviewer", cell: (v) => <Mono>{shortId(v.actor_user_id)}</Mono> },
            { key: "decision", header: "Decision", cell: (v) => v.decision.replace(/_/g, " ") },
            { key: "result", header: "Resulted in", cell: (v) => v.resulted_in.replace(/_/g, " ") },
            { key: "att", header: "Attached then", cell: (v) => <Mono>{v.attached_object_id_at_decision ?? "none"}</Mono> },
            { key: "reason", header: "Reason", cell: (v) => v.reason },
            { key: "at", header: "When", cell: (v) => when(v.decided_at) },
          ]}
        />
      </Card>
      <OperationDecision op={op} />
    </div>
  );
}

function effectOf(op: ProviderOperation, decision: ProviderDecision): ReactNode {
  const m = money(op);
  const amt = <Amount value={m.amount} currency={m.currency} />;
  const payout = op.operation_type === "payout";
  const refund = op.operation_type === "refund";
  switch (decision) {
    case "confirmed":
      return payout ? (
        <>Finalizes the payout as paid: {amt} left for the publisher's Connect account.</>
      ) : refund ? (
        <>Finalizes the refund as sent: {amt} left for the advertiser's card.</>
      ) : (
        <>Finalizes the transfer reversal as done: {amt} came back from the publisher.</>
      );
    case "failed":
      return payout ? (
        <>Finalizes the payout as failed: {amt} goes back from payout liability to the publisher's claimable balance.</>
      ) : refund ? (
        <>Finalizes the refund as failed: the encumbered {amt} is released back to the advertiser's wallet.</>
      ) : (
        <>Finalizes the transfer reversal as failed: {amt} was not recovered.</>
      );
    case "double_payment":
      return payout ? (
        <>The money left although the payout was finalized failed: {amt} is taken back from the publisher's payable balance; any shortfall becomes publisher debt.</>
      ) : (
        <>The money left although the refund was finalized failed: {amt} is taken back from the advertiser's held or spendable funds; any shortfall becomes a receivable.</>
      );
    default:
      return "Clears the flag. No money moves.";
  }
}

const DECISION_LABEL: Record<ProviderDecision, string> = {
  confirmed: "Confirmed: the money moved",
  failed: "Failed: the money didn't move",
  acknowledged: "Acknowledge the flag",
  double_payment: "Double payment: it was finalized failed but the money left",
};

function OperationDecision({ op }: { op: ProviderOperation }) {
  const ep = useEndpoints();
  const qc = useQueryClient();
  const options = allowedDecisions(op);
  const [decision, setDecision] = useState<ProviderDecision | "">("");
  const [objectId, setObjectId] = useState("");
  const [reason, setReason] = useState("");
  const [evidence, setEvidence] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<ProviderDecisionRequest | null>(null);
  const cmd = useStaffCommand(
    (req: ProviderDecisionRequest, opts) => ep.staff.providerOperations.decide(op.provider_operation_id, req, opts),
    () => {
      setConfirming(null);
      void qc.invalidateQueries({ queryKey: staffKeys.providerOps });
      void qc.invalidateQueries({ queryKey: ["staff-provider-op", op.provider_operation_id] });
    },
  );
  const res: ProviderDecisionResponse | undefined = cmd.data;

  if (!options.length) return null;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!decision) return setFormError("Choose a decision.");
    const obj = decision === "double_payment" && !objectId.trim() ? (op.provider_object_id ?? "") : objectId.trim();
    if ((decision === "confirmed" || decision === "double_payment") && !obj) return setFormError("Name the Stripe object that shows the money moved.");
    const re = reasonError(reason);
    if (re) return setFormError(re);
    const ev = parseEvidence(evidence, { required: false });
    if (!ev.ok) return setFormError(ev.error);
    setFormError(null);
    cmd.reset();
    const req: ProviderDecisionRequest = { decision, provider_object_id: obj, reason: reason.trim(), evidence: ev.value };
    if (decision === "acknowledged") cmd.run(req);
    else setConfirming(req);
  }

  const m = money(op);
  const votesFor = confirming?.decision === "failed" ? countingVotes(op, "failed") : confirming?.decision === "double_payment" ? countingVotes(op, "double_payment") : [];
  const firstOfTwo = (confirming?.decision === "failed" || confirming?.decision === "double_payment") && votesFor.length === 0;

  return (
    <Card title="Decision">
      {res?.result === "awaiting_second_approval" ? <AwaitingSecondBanner approvals={res.approvals} required={res.approvals_required} /> : null}
      {res && res.result !== "awaiting_second_approval" ? (
        <Banner tone="success" live="polite" title={`Recorded: ${res.result.replace(/_/g, " ")}`}>
          <Link to="/staff/provider-operations">Back to the list</Link>
        </Banner>
      ) : null}
      <form className="stack" onSubmit={submit} noValidate>
        <RadioGroup legend="Decision" name="provider-decision" value={decision} onChange={setDecision} options={options.map((o) => ({ value: o, label: DECISION_LABEL[o] }))} />
        {decision === "confirmed" || decision === "double_payment" || decision === "failed" ? (
          <Input
            label="Stripe object id"
            mono
            optional={decision === "failed"}
            value={objectId}
            placeholder={decision === "double_payment" ? (op.provider_object_id ?? "") : op.operation_type === "payout" ? "tr_…" : op.operation_type === "refund" ? "re_… or pyr_…" : "trr_…"}
            onChange={(e) => setObjectId(e.target.value)}
            hint={decision === "double_payment" ? "Must be the attached object." : "Verify it in the Stripe dashboard first."}
          />
        ) : null}
        <Textarea label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} />
        <Textarea
          label="Evidence (JSON object)"
          optional
          hint='What you checked, e.g. {"stripe_dashboard": "checked 2026-09-25", "ticket": "OPS-12"}.'
          value={evidence}
          onChange={(e) => setEvidence(e.target.value)}
          rows={2}
          className="mono"
        />
        <StepUpNote required />
        {formError ? <Banner tone="error" live="alert">{formError}</Banner> : null}
        {cmd.error && !confirming ? <StaffRejection error={cmd.error} /> : null}
        <div>
          <Button type="submit" loading={cmd.isPending && !confirming}>
            Record decision
          </Button>
        </div>
      </form>
      <MoneyConfirmDialog
        open={!!confirming}
        title={confirming ? `Record "${confirming.decision.replace(/_/g, " ")}"?` : ""}
        amount={m.amount ?? "0"}
        currency={m.currency}
        effect={confirming ? effectOf(op, confirming.decision) : null}
        pendingSecond={firstOfTwo && "Failed and double-payment decisions always need two distinct reviewers."}
        confirmLabel={firstOfTwo ? "Record my vote" : "Confirm decision"}
        busy={cmd.isPending}
        error={confirming ? cmd.error : undefined}
        onCancel={() => setConfirming(null)}
        onConfirm={() => confirming && cmd.run(confirming)}
      />
    </Card>
  );
}
